import { useMemo, useState, useCallback, useLayoutEffect, useRef } from "react";
// Components
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { ISearchPairing, PolicyAction, hasPermission, IBidPackage } from "@vmosolutions/oc-interfaces";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
// Import tableData definitions from search categories.
import { ITableData, tableData } from "./SearchCategories";
import { Grid, Typography, Button, LinearProgress } from "@mui/material"; // Typography, Button
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { ICrewBid } from "../helpers";
import _ from "lodash";

interface IPairingSearchProps {
  packageId: string;
  path: string[];
  id: string;
  setBidPairingCount: (bidPairings: { pairingId: string; bidPairingId: string }[]) => void; // refresh package if user has added new bid pairings.
  bidPairingprogressPercentage: number;
}

export interface ITableColumn {
  id: string;
  value: string;
  index: number;
  numeric: boolean;
  label: string;
  type: string;
  width: number;
  visible: boolean;
  filter: boolean;
}

interface IAddPairingResponse {
  bidPackage: IBidPackage;
  bidPairings: { pairingId: string; bidPairingId: string }[];
  crewBids: ICrewBid[];
  failed: string[];
  succeeded: string[];
}

export const PairingSearch = (props: IPairingSearchProps): JSX.Element => {
  const { packageId, id, path, setBidPairingCount, bidPairingprogressPercentage } = props;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<any>(null);
  const dispatch = useDispatch();

  // Store selected pairing ids in state and prompt user to add them to bid pairing.
  const [selectedPairingIds, setSelectedPairingIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const mosaicPanel = useMemo(() => `${id}-panel`, [id]);
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);

  const handleAddPairings = useCallback(async (): Promise<void> => {
    setSubmitting(true);
    //console.log("payload", values);
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Adding Bid Pairings To Package...",
      successMessage: "Bid Pairings Added To Package Successfully",
      errorMessage: "Error: Unable to Add Bid Pairings To Package, try a smaller selection set.",
    };
    const response: IAddPairingResponse = await AmplifyAPIWrapper.Put<any>(
      `/bidding/package/${packageId}/pairings`,
      selectedPairingIds,
      notifications
    );
    // Pairings have been successfully added, refresh the package to update summary.
    // Disable this add pairings button if the user has unsaved changed in the package editor.
    if ((response?.succeeded?.length || response?.succeeded?.length) && Array.isArray(response?.bidPairings)) {
      setBidPairingCount(response?.bidPairings);
      setSelectedPairingIds([]);
    }
    setSubmitting(false);
  }, [dispatch, packageId, selectedPairingIds, setBidPairingCount]);

  /**
   * SELECT + FOCUS? events selected in the menu
   * @param events - events to select in timeline
   * @param focusSelected - whether or not to focus on those events (zoom contain)
   * @param dataType - the type of event i.e. pairings, flights etc.
   */
  const handleSelectPairings = useCallback(
    (events: ISearchPairing[], focusSelected?: boolean, dataType?: string): void => {
      switch (dataType) {
        case "pairings": {
          // console.log("pairings", events);
          const addPairingsIds = events?.length ? events?.map((pairing) => pairing?._id) : [];
          setSelectedPairingIds(addPairingsIds);
          break;
        }
        default:
        // code block
      }
    },
    []
  );

  const allowedTableData: ITableData[] = useMemo(
    () => tableData.filter((data: ITableData) => hasPermission(policies, data.permissionResource, PolicyAction.Read)),
    [policies]
  );

  const [containerHeight, setContainerHeight] = useState<number>(0);
  useLayoutEffect(() => {
    setTimeout(() => {
      const container = tableContainerRef?.current?.getBoundingClientRect();
      const tableHeight = container?.height ? Math.round(container?.height) - 150 : 0;
      // console.log(tableHeight);
      setContainerHeight(tableHeight);
    }, 100);
  }, [path, tableContainerRef]);

  const handleClearSelection = useCallback(() => {
    setSelectedPairingIds([]);
    if (tableRef?.current?.hasOwnProperty("clearSelectedrows")) {
      tableRef.current.clearSelectedrows();
    }
  }, [tableRef]);

  //if some how you open this window and have no allowed table data
  if (!allowedTableData.length) {
    return null;
  }
  return (
    <Grid container direction="column" ref={tableContainerRef}>
      <FilterSearchTable
        ref={tableRef}
        id={id}
        path={path}
        className="pairing_search_table"
        events={allowedTableData}
        parentId={mosaicPanel}
        uniqueKey={"_id"}
        changeSelectedRows={handleSelectPairings}
        allowMultiFilter={true}
        showDateRangeRRule={true}
        showSavedFilters={true}
        hideFocusSelected={true}
        maxTableHeight={containerHeight}
        defaultRowsPerPage={25}
        optionalControls={
          <Grid container direction="column">
            <Grid container alignItems="center" className="add_pairing_container">
              <Typography variant="caption" color="textPrimary" style={{ margin: "0px 10px" }}>
                ({selectedPairingIds?.length > 0 ? selectedPairingIds?.length : "No"} pairings selected)
              </Typography>
              <Typography variant="caption" color="inherit">
                {selectedPairingIds?.length > 0
                  ? "New snapshots will be created for any duplicates already in package."
                  : "Use the search below to find and select pairings to add."}
              </Typography>
              <Button
                className="refresh_btn"
                variant="contained"
                color="primary"
                onClick={_.debounce(handleAddPairings, 500)}
                disabled={!selectedPairingIds?.length || submitting}
              >
                Add to Bid Package
              </Button>
              <Button
                className="refresh_btn"
                variant="outlined"
                onClick={() => handleClearSelection()}
                disabled={!selectedPairingIds?.length || submitting}
              >
                Clear Selection
              </Button>
            </Grid>
            <Grid item className="loading_bar" style={{ width: "100%", margin: "10px 0px 0px -10px" }}>
              <LinearProgress variant="determinate" value={bidPairingprogressPercentage} />
            </Grid>
          </Grid>
        }
        // hideSelectionCheckboxes={true}
      />
    </Grid>
  );
};
